import React from 'react';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';

const PAGE_SIZE_OPTIONS = [12, 24, 48];

// Build compact page list like [1, 2, '…', 5, 6, 7, '…', 12]
const buildPageList = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, 2, total - 1, total, current - 1, current, current + 1]);
  const sorted = Array.from(pages).filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push('…');
    out.push(p);
  });
  return out;
};

export const Pagination = ({
  currentPage,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onLoadMore,
  hasMore,
  loading,
  loadedCount,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const pages = buildPageList(currentPage, totalPages);
  const start = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const displayed = typeof loadedCount === 'number' ? loadedCount : Math.min(pageSize, totalItems - (currentPage - 1) * pageSize);
  const end = (currentPage - 1) * pageSize + displayed;

  return (
    <div
      className="mt-10 space-y-6 border-t border-white/5 pt-8"
      data-testid="pagination"
    >
      {/* Top row: counts + page size */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className="text-xs uppercase tracking-widest text-stone-500">
          Showing <span className="text-stone-300">{start}–{end}</span> of{' '}
          <span className="text-stone-300">{totalItems}</span>
        </p>

        <div className="flex items-center gap-3">
          <label className="text-xs uppercase tracking-widest text-stone-500">Per page</label>
          <Select
            value={String(pageSize)}
            onValueChange={(v) => onPageSizeChange(Number(v))}
          >
            <SelectTrigger
              className="bg-stone-900/50 border-stone-700 rounded-none h-9 w-24"
              data-testid="page-size-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-stone-900 border-stone-700">
              {PAGE_SIZE_OPTIONS.map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Load More button */}
      {hasMore && onLoadMore && (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="outline"
            onClick={onLoadMore}
            disabled={loading}
            className="border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10 hover:text-[#d4af37] rounded-none uppercase tracking-widest text-xs px-8 h-11"
            data-testid="load-more-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {loading ? 'Loading…' : 'Load More'}
          </Button>
        </div>
      )}

      {/* Numbered pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 flex-wrap">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={currentPage === 1}
            onClick={() => onPageChange(currentPage - 1)}
            className="text-stone-400 hover:text-[#d4af37] disabled:opacity-30 rounded-none"
            data-testid="pagination-prev"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Prev
          </Button>

          {pages.map((p, idx) =>
            p === '…' ? (
              <span key={`ellipsis-${idx}`} className="px-2 text-stone-600">…</span>
            ) : (
              <Button
                key={p}
                type="button"
                variant={p === currentPage ? 'default' : 'ghost'}
                size="sm"
                onClick={() => onPageChange(p)}
                className={
                  p === currentPage
                    ? 'bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none min-w-[2.25rem] h-9'
                    : 'text-stone-400 hover:text-[#d4af37] rounded-none min-w-[2.25rem] h-9'
                }
                data-testid={`pagination-page-${p}`}
              >
                {p}
              </Button>
            )
          )}

          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={currentPage === totalPages}
            onClick={() => onPageChange(currentPage + 1)}
            className="text-stone-400 hover:text-[#d4af37] disabled:opacity-30 rounded-none"
            data-testid="pagination-next"
          >
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default Pagination;
